import Vue from 'vue'
import Vuex from 'vuex'
import axios from 'axios'
import { API } from '../api/common'
import common_transformer from '../api/common-transformer.js'
import t1 from '../api/pos-order-transformer.js'
import t2 from '../api/sales-invoice-transformer.js'
import t3 from '../api/localserver-transformer.js'
import t4 from '../api/localstorage-transformer.js'
import * as localForage from "localforage";
import { v4 as uuidv4 } from 'uuid'

const transformers = {
  'POS Order': t1,
  'Sales Invoice': t2,
  'Local Server': t3,
  'Local Storage': t4
}

const default_settings = {
  pre_orders: true,
  paid_orders: false,
  dark_mode: false,
  hours_since: 200,
  heart_beat: 20,
  enable_preparing: true,
  syncing: true,
  is_grouped: false,
  enable_sync_completion_status: false,
  bump_bar_mode: 'Keyboard',
  serial_baud_rate: 9600,
}

const resolveKdsId = (state) => {
  let kds_id = ''
  let local_settings = JSON.parse(localStorage?.getItem("kds_settings"))
  
  if(state?.settings?.enable_multi_kds_setup){
    if(state?.settings?.kds_id){
      kds_id = state.settings.kds_id
    } else if(local_settings?.kds_id) {
      kds_id = local_settings.kds_id
    } else {
      kds_id = uuidv4()
      if(local_settings){
        local_settings['kds_id'] = kds_id
      } else {
        local_settings = {'kds_id': kds_id}
      }
      localStorage.setItem("kds_settings", JSON.stringify(local_settings))
    }
  }
  return kds_id
}

const default_key_map = {
  'BUMP_1': 'a',
  'BUMP_2': 'c',
  'BUMP_3': 'e',
  'BUMP_4': 'g',
  'BUMP_5': 'b',
  'BUMP_6': 'd',
  'BUMP_7': 'f',
  'BUMP_8': 'h',
  'NAV_LEFT': 'o',
  'NAV_RIGHT': 'p',
  'ACTION_REFRESH': 'i',     
  'SPLIT_ON_OFF': 'j',        
  'QUE_SELECT': 'k',     
  'QUE_ON_OFF': 'l',     
  'RECALL_LAST': 'm',  
  'SUM_ON_OFF': 'n'
};

Vue.use(Vuex)

export default new Vuex.Store({
  state: {
    drawer: null,
    settings_dialog: false,
    settings: {
      ...default_settings,
      ...JSON.parse(localStorage?.getItem("kds_settings") || "{}"),
    },
    loading: false,
    show_hidden: false,
    orders: {},
    undo_stack: [],
    redo_stack: [],
    is_on_hold: localStorage.getItem('is_on_hold') === 'true',
    bumpbar_map: JSON.parse(localStorage.getItem('kds_bumpbar_map')) || default_key_map,
    synced_completed_orders: new Set(),
    secondary_order_ids: JSON.parse(localStorage.getItem('kds_secondary_map') || '{}')
  },
  getters: {
    sync_enabled(state) {
      return state.settings.syncing && !state.settings.is_grouped
    },
  },
  mutations: {
    ADD_TO_ORDER_VERSION (state, {order_id, n}) {
      const version = (state.orders[order_id].version ?? 0) + n
      Vue.set(state.orders[order_id], 'version', version >= 0 ? version : 0)
    },
    SET_ORDER_STATUS (state, {order_id, status}) {
      Vue.set(state.orders[order_id], 'hidden', status)
      state.undo_stack.push({order_id, status})
      state.redo_stack = []
    },
    UNDO (state) {
      if (state.undo_stack.length === 0) {
        return
      }
      const {order_id, status} = state.undo_stack.pop()
      Vue.set(state.orders[order_id], 'hidden', !status)
      state.redo_stack.push({order_id, status})
    },
    REDO (state) {
      if (state.redo_stack.length === 0) {
        return
      }
      const {order_id, status} = state.redo_stack.pop()
      Vue.set(state.orders[order_id], 'hidden', status)
      state.undo_stack.push({order_id, status})
    },
    SET_ITEM_STATUS (state, {order_id, item_idx, status}) {
      Vue.set(state.orders[order_id].items[item_idx], 'status', status)
    },
    SET_DRAWER (state, payload) {
      state.drawer = payload
    },
    SET_SHOW_HIDDEN (state, payload) {
      state.show_hidden = payload
    },
    SET_SETTINGS(state, payload) {
      state.settings_dialog = payload
    },
    
    SET_HOLD_STATUS(state, status) {
      state.is_on_hold = status
      localStorage.setItem('is_on_hold', status)
    },

    UPDATE_SETTINGS(state, settings) {
      state.settings = { ...state.settings, ...settings }
    },

    SET_BUMP_KEY(state, { action, key }) {
      state.bumpbar_map = {
        ...state.bumpbar_map,
        [action]: key
      };
      localStorage.setItem('kds_bumpbar_map', JSON.stringify(state.bumpbar_map));
    },
    RESET_BUMP_MAP(state) {
      state.bumpbar_map = { ...default_key_map };
      localStorage.setItem('kds_bumpbar_map', JSON.stringify(state.bumpbar_map));
    },
    ADD_SECONDARY_ORDER_IDS(state, ids) {
      ids.forEach(id => Vue.set(state.secondary_order_ids, id, true))
      localStorage.setItem('kds_secondary_map', JSON.stringify(state.secondary_order_ids))
    },
  },
  actions: {
    async LOAD_ORDERS(context, payload) {
      const state = context.state
      if (!payload) {
        state.loading = true
      }

      const kds_id = resolveKdsId(state)
      const selectedTransformer = state?.settings?.selected_transformer ?? 'POS Order'
      const params = {
        hours_since: state?.settings?.hours_since ?? 2000,
        item_groups: JSON.stringify(state?.settings?.selected_groups) ?? '',
        include_paid: state?.settings?.paid_orders,
        kds_id
      }

      const applyFilter = (orders) => {
        if (payload != null && typeof payload == 'string') {
          return Object.fromEntries(Object.entries(orders).filter(order => order[0].caption && order[0].caption.includes(payload)))
        }
        return orders
      }

      const fetchPrimary = async () => {
        const endpoint = state?.settings?.server_endpoint ?? 'api/method/pos_multi_currency.pos_multi_currency.controllers.api.get_online_orders'
        const response = await API.get(endpoint, { params })
        let orders = await transformers[selectedTransformer](response)
        orders = applyFilter(orders)
        return await common_transformer(orders)
      }

      const fetchSecondary = async () => {
        const host = (state?.settings?.secondary_local_server_host || '').replace(/\/$/, '')
        const endpoint = (state?.settings?.secondary_server_endpoint || '').replace(/^\//, '')
        if (!host || !endpoint) return {}
        const url = `${host}/${endpoint}`
        const response = await axios.get(url, { params, timeout: 10000 })
        let orders = await transformers[selectedTransformer](response)
        orders = applyFilter(orders)
        return await common_transformer(orders)
      }

      try {
        const [primaryResult, secondaryResult] = await Promise.allSettled([
          fetchPrimary(),
          fetchSecondary()
        ])

        if (primaryResult.status === 'rejected') console.log('Primary fetch failed:', primaryResult.reason)
        if (secondaryResult.status === 'rejected') console.log('Secondary fetch failed:', secondaryResult.reason)

        const primaryOrders = primaryResult.status === 'fulfilled' ? primaryResult.value : {}
        const secondaryOrders = secondaryResult.status === 'fulfilled' ? secondaryResult.value : {}

        // Register secondary IDs (additive-only within session)
        const newSecondaryIds = Object.keys(secondaryOrders)
        if (newSecondaryIds.length) {
          context.commit('ADD_SECONDARY_ORDER_IDS', newSecondaryIds)
        }

        // Merge: primary takes precedence on ID collision
        const merged = { ...secondaryOrders, ...primaryOrders }

        let set_orders = true
        for (const oid of Object.keys(merged)) {
          if ((state?.orders?.[oid]?.version ?? 0) > (merged[oid]?.version ?? 0)) {
            set_orders = false
            break
          }
        }

        if (set_orders) {
          state.orders = merged
        }
      } catch (e) {
        console.log(e)
      } finally {
        state.loading = false
      }
    },
    async SAVE_MODIFICATIONS_LOCALLY({state}) {
      const modifications = {}
      for (const oid of Object.keys(state.orders)) {
        const order = state.orders[oid]
        modifications[oid] = {}
        modifications[oid].hidden = order.hidden
        modifications[oid].items = {}
        for (const item of order.items) {
          modifications[oid].items[item.row_id] = item.status
        }
      }
      await localForage.setItem("non-syncing-kds-modifications", JSON.stringify(modifications));
    },
    async SYNC_ORDER(context, {order_id, on_success, on_fail, kds_id, hold}) {
      const {state, commit, dispatch} = context
      state.loading = true
      order_id && commit('ADD_TO_ORDER_VERSION', {order_id, n: 1})

      const isSecondary = order_id ? !!state.secondary_order_ids[order_id] : false

      try {
        kds_id = kds_id || resolveKdsId(state)
        let args = {kds_id}
        if(typeof hold !== 'undefined'){
          args["hold"] = hold
        }
        if(order_id){
          args["invoice"] = JSON.stringify(state.orders[order_id])
        }

        if (isSecondary) {
          const host = (state.settings.secondary_local_server_host || '').replace(/\/$/, '')
          const endpoint = (state.settings.secondary_server_endpoint || '').replace(/^\//, '')
          await axios.post(`${host}/${endpoint}`, args)
        } else {
          const endpoint = state.settings?.server_endpoint ?? 'api/method/pos_multi_currency.pos_multi_currency.doctype.pos_order.pos_order.sync/docs/'
          await API.post(endpoint, args)
        }

        on_success()
        state.loading = false
        return
      } catch (e) {
        order_id && commit('ADD_TO_ORDER_VERSION', {order_id, n: -1})
        try {
          const result = JSON.parse(e?.response?.data?._server_messages ?? `["{\\"message\\": \\"${e?.response?.data ?? "Fail"}\\"}"]`)
            .map(r => JSON.parse(r).message).join("\n")
          on_fail(e, result)
        } finally {
          dispatch('LOAD_ORDERS', false)
        }
      }
    },

    async toggleKdsHold({ commit, state }, { on_success, on_fail } = {}) {
      const kds_id = resolveKdsId(state)
      if (!kds_id) {
        if (on_fail) on_fail("KDS ID Undefined")
        return
      }

      const current_status = state.is_on_hold
      const new_status = !current_status

      commit('SET_HOLD_STATUS', new_status)

      const body = JSON.stringify({ kds_id, is_on_hold: new_status })
      const headers = { 'Content-Type': 'application/json' }

      const postToHost = (rawHost) => {
        if (!rawHost) return Promise.resolve()
        const host = rawHost.replace(/\/$/, '')
        return fetch(`${host}/kds/config`, { method: 'POST', headers, body })
      }

      const primaryHost = state.settings.local_server_host || 'http://localhost:9200'
      const secondaryHost = state.settings.secondary_local_server_host || ''

      try {
        await postToHost(primaryHost)

        // Fire-and-forget to secondary — its failure must not affect primary
        if (secondaryHost) {
          postToHost(secondaryHost).catch(e => console.error('Secondary hold toggle failed:', e))
        }

        if (on_success) on_success(new_status)
        localStorage.setItem("is_on_hold", new_status)

      } catch (error) {
        console.error("Hold Toggle Error", error)
        commit('SET_HOLD_STATUS', current_status)
        if (on_fail) on_fail("Failed to connect to Local Server at " + primaryHost)
      }
    },

    async SAVE_ORDER(context, {order_id, on_success, on_fail, kds_id, hold}) {
      const {getters, dispatch} = context

      if (!getters.sync_enabled) {
        dispatch('SAVE_MODIFICATIONS_LOCALLY')
      } else {
        dispatch('SYNC_ORDER', {order_id, on_success, on_fail, kds_id, hold})
      }

      try {
        dispatch('SEND_SMS', {order_id})
      } catch (e) {
        console.log("Error while sending sms message >>> ", e)
      }
    },
    SYNC_ORDER_STATUS_TO_LOCAL({ state }, { order_id }) {
      if (!state.settings.enable_sync_completion_status) return
      const order = state.orders[order_id]
      if (!order) return

      const doc_id = order.offline_pos_name || order._id
      const kds_status = order.hidden ? 'DONE' : 'PENDING'

      const isSecondary = !!state.secondary_order_ids[doc_id]
      const rawHost = isSecondary
        ? (state.settings.secondary_local_server_host || '')
        : (state.settings.local_server_host || 'http://localhost:9200')

      if (!rawHost) return
      const host = rawHost.replace(/\/$/, '')

      // Fire-and-forget: never block the caller
      fetch(`${host}/kds/order_status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          offline_pos_name: doc_id,
          kds_status
        })
      }).then(() => {
        if (order.hidden) {
          state.synced_completed_orders.add(doc_id)
        } else {
          state.synced_completed_orders.delete(doc_id)
        }
      }).catch(error => {
        console.error('Failed to sync order status for order:', order_id, error)
      })
    },

    SYNC_ALL_COMPLETED_TO_LOCAL({ state, dispatch }) {
      if (!state.settings.enable_sync_completion_status) return

      const completed_order_ids = Object.keys(state.orders).filter(oid => {
        const order = state.orders[oid]
        const doc_id = order.offline_pos_name || order._id
        return order.hidden && !state.synced_completed_orders.has(doc_id)
      })

      // Fire all in parallel, no awaiting
      completed_order_ids.forEach(order_id => {
        dispatch('SYNC_ORDER_STATUS_TO_LOCAL', { order_id })
      })
    },

    async SEND_SMS(context, {order_id}){
      const {state} = context
      if(state.settings && state.settings.send_sms && state.settings.sms_sender_name && state.settings.sms_message
        && state.orders[order_id].customer_mobile && state.orders[order_id].hidden
      ){
        API.post('api/method/frappe.core.doctype.sms_settings.sms_settings.send_sms', {
          receiver_list: JSON.stringify([state.orders[order_id].customer_mobile]), msg: state.settings.sms_message, 
          sender_name: state.settings.sms_sender_name, sms_account: state.settings.sms_account
        }).then(() => {
          window.toast.show({
            message: `SMS sent successfully 📨 ✅`,
            type: "success"
          })
        }).catch(() => {
          window.toast.show({
            message: `Failed to send SMS 📮`,
            type: "error"
          })
        })
      }
    },
  },
  modules: {
  }
})